import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  IconButton,
  Avatar,
  CircularProgress
} from '@mui/material';
import { Edit, Delete, Add, Business } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { sponsorService } from '../services/sponsorService';
import { Sponsor } from '../types';
import { AppHeader } from '../components/AppHeader';

const SponsorList: React.FC = () => {
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      setLoading(true);
      const data = await sponsorService.getSponsorsByAssociation();
      setSponsors(data);
    } catch (error) {
      console.error('Erro ao carregar patrocinadores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este patrocinador?')) {
      try {
        await sponsorService.deleteSponsor(id);
        await fetchSponsors();
      } catch (error) {
        console.error('Erro ao excluir patrocinador:', error);
      }
    }
  };

  return (
    <>
      <AppHeader 
        title="Patrocinadores" 
        subtitle="Gerencie os patrocinadores das campanhas promocionais"
        icon={<Business />}
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Lista de Patrocinadores
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/sponsors/new')}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Novo Patrocinador
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Carregando patrocinadores...
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.light' }}>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Logo</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Data de Criação</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sponsors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Business sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Nenhum patrocinador encontrado
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Comece criando seu primeiro patrocinador para ver a lista aqui.
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => navigate('/sponsors/new')}
                          sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            fontWeight: 600,
                            textTransform: 'none'
                          }}
                        >
                          Criar Primeiro Patrocinador
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  sponsors.map((sponsor) => (
                    <TableRow key={sponsor.id}>
                      <TableCell>
                        <Avatar
                          src={sponsor.logoUrl}
                          alt={sponsor.name}
                          sx={{ width: 40, height: 40 }}
                        >
                          <Business />
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {sponsor.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(sponsor.createdAt).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/sponsors/${sponsor.id}/edit`)}
                            sx={{
                              borderRadius: 2,
                              '&:hover': { backgroundColor: 'primary.light' }
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(sponsor.id)}
                            color="error"
                            sx={{
                              borderRadius: 2,
                              '&:hover': { backgroundColor: 'error.light' }
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </>
  );
}; 
export default SponsorList;